/**
 * 이상한 방 - 리빌 공개 순서
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.2절(제거가 최고 난도) · 5절(웃김 설계 포인트 6번)
 *
 * ## 왜 `reveal.ts`에서 뽑았는가
 *
 * `reveal.ts`는 카메라(`three`)와 라벨(`i18n`)을 안다. 서버가 그 파일을 import 하는 순간
 * DOM lib 없는 typecheck가 막히고, 무엇보다 **순서는 규칙이고 카메라는 연출**이다 - 매치 호스트가
 * 리빌 스텝 인덱스를 쥐려면(상태모델 11.3절) 순서를 씬 없이 계산할 수 있어야 한다.
 * 그래서 정렬만 여기로 내리고, `reveal.ts`는 이 결과를 받아 화각·라벨을 얹는다.
 *
 * ## 마지막 자리를 무엇에 주는가 - `finale`
 *
 *  - `'hardest'` - 싱글. 미발견 안에서 쉬운 것 → 어려운 것. 수색이 끝난 직후가 긴장의 최고점이고,
 *    뒤로 갈수록 교묘해져야 마지막이 가장 크게 터진다("저걸 못 봤다고???").
 *  - `'easiest'` - 파티. 미발견 안에서 어려운 것 → 쉬운 것. 여러 사람이 **다 같이** 놓친 것 중
 *    가장 쉬웠던 것이 마지막에 오면 "저걸 아무도 못 봤다고???"가 된다 - 웃음의 주어가
 *    한 사람의 실수에서 전원의 실수로 옮겨 가는 것이 파티 리빌의 드라마다.
 *
 * 어느 쪽이든 **미발견 전부가 먼저**, 발견분은 찾은 순서대로 뒤에 붙는다 -
 * 이미 찾은 것부터 훑으면 긴장이 먼저 새어 나간다. 발견분은 점수 집계처럼 소비된다.
 */

import { compareCodePoint } from '../core/order'
import type { Change, ChangeDifficulty, ChangeVerb } from './changeGen'

/* --- 타입 ----------------------------------------------------------------------- */

/** 마지막 스텝에 무엇을 놓는가. 위 머리말의 두 드라마. */
export type RevealFinale = 'hardest' | 'easiest'

/**
 * 발견 기록에서 순서가 읽는 두 필드. `gameState.FoundEntry`와 매치의 공유 발견 목록이
 * 둘 다 구조적으로 만족하므로 어댑터 없이 넘긴다.
 */
export type FoundLike = Readonly<{ changeId: string; foundAtMs: number }>

/** 순서가 매겨진 변화 하나. `foundAtMs`는 미발견이면 0이다(정렬에 쓰이지 않는다). */
export type OrderedChange = Readonly<{ change: Change; found: boolean; foundAtMs: number }>

/* --- 순위표 --------------------------------------------------------------------- */

/**
 * 난이도 순위. 뒤로 갈수록 놀랍다.
 * `difficulty`는 `changeGen`의 선택 필드라 없을 수도 있으므로 동사 순위와 합산한다.
 */
export const DIFFICULTY_RANK: Readonly<Record<ChangeDifficulty, number>> = { easy: 0, normal: 1, hard: 2 }

/**
 * 동사별 "놀람" 순위.
 *
 * 추가는 새 물건이 눈에 띄어 가장 순하고, 제거는 컨셉시트 4.2절이 최고 난도로 지목했다 -
 * **없어진 것은 생긴 것보다 알아채기 어렵다.** 그래서 제거가 마지막 자리를 가져간다.
 */
export const VERB_SURPRISE_RANK: Readonly<Record<ChangeVerb, number>> = {
  add: 0,
  recolor: 1,
  move: 2,
  rotate: 3,
  remove: 4,
}

/**
 * 동사 순위가 차지하는 자릿수. 난이도 한 단계가 동사 **전부**보다 커야 "난이도가 먼저, 동사가 나중"이
 * 성립한다. `reveal.ts`에 있던 원본은 여기에 최대 순위(4)를 곱했는데, 그러면 easy+remove(4)와
 * normal+add(4)가 같은 값이 되어 id 순으로 갈렸다 - 주석이 약속한 사전식 순서가 아니었다.
 * 최대 순위 + 1을 곱해야 자릿수가 겹치지 않는다.
 */
const VERB_RANK_SPAN = VERB_SURPRISE_RANK.remove + 1

/* --- 순서 ----------------------------------------------------------------------- */

/**
 * 공개 순서를 정한다. **미발견 전부가 먼저**, 그 뒤에 발견분이 붙는다.
 *
 * 미발견 안의 방향은 `finale`이 정한다(머리말). 발견분은 찾은 순서대로 - 술래가 라운드를
 * 되감아 보는 순서라 "아 저거 내가 두 번째로 찾았지"가 그대로 재생된다.
 * 같은 순위끼리는 `id`로 갈라 **같은 상태면 항상 같은 순서**가 되게 한다(결정론).
 * `id` 타이브레이크는 `finale`과 무관하게 오름차순이다 - 뒤집을 이유가 없는 것을 뒤집으면
 * 두 모드의 순서를 나란히 놓고 비교할 때 노이즈가 된다.
 */
export function orderChanges(
  changes: readonly Change[],
  found: readonly FoundLike[],
  finale: RevealFinale,
): readonly OrderedChange[] {
  const foundAt = new Map(found.map((entry) => [entry.changeId, entry.foundAtMs]))

  const unfound: OrderedChange[] = []
  const foundList: OrderedChange[] = []

  for (const change of changes) {
    const at = foundAt.get(change.id)
    if (at === undefined) unfound.push({ change, found: false, foundAtMs: 0 })
    else foundList.push({ change, found: true, foundAtMs: at })
  }

  const direction = finale === 'hardest' ? 1 : -1
  unfound.sort(
    (a, b) => direction * (surpriseRank(a.change) - surpriseRank(b.change)) || compareId(a, b),
  )
  foundList.sort((a, b) => a.foundAtMs - b.foundAtMs || compareId(a, b))

  return [...unfound, ...foundList]
}

/** 변화 하나의 "놀람" 점수. 클수록 교묘하다. */
export function surpriseRank(change: Change): number {
  const byDifficulty = change.difficulty === undefined ? 0 : DIFFICULTY_RANK[change.difficulty]
  // 난이도가 같으면 동사가 순서를 가른다. 난이도 필드가 아예 없는 픽스처에서도 동작해야 한다.
  return byDifficulty * VERB_RANK_SPAN + VERB_SURPRISE_RANK[change.verb]
}

/** id 순. 로케일 무관 비교자를 쓰는 이유는 `core/order.ts` 머리말. */
function compareId(a: OrderedChange, b: OrderedChange): number {
  return compareCodePoint(a.change.id, b.change.id)
}
